import { EmojiData, Emoji } from '@appTypes/emoji';
import enTranslation from '../locales/en/translation.json';

type TranslationDict = Record<string, unknown>;

function resolveKey(dict: TranslationDict, key: string): string {
  const parts = key.split('.');
  let node: unknown = dict;
  for (const part of parts) {
    if (node == null || typeof node !== 'object') return '';
    node = (node as TranslationDict)[part];
  }
  return typeof node === 'string' ? node : '';
}

function flattenEmojis(data: EmojiData): Emoji[] {
  const result: Emoji[] = [];
  for (const category of data.categories) {
    if (category.subgroups) {
      for (const subgroup of category.subgroups) {
        result.push(...subgroup.emojis);
      }
    } else if (category.emojis) {
      result.push(...category.emojis);
    }
  }
  return result;
}

function buildSearchTextForLocales(emoji: Emoji, locales: TranslationDict[]): string {
  const parts: string[] = [];
  for (const locale of locales) {
    parts.push(resolveKey(locale, emoji.nameKey));
    for (const kw of emoji.keywords) {
      parts.push(resolveKey(locale, kw));
    }
  }
  return parts.join(' ').toLowerCase();
}

// Build initial index with EN only (fast, synchronous)
export function buildSearchIndex(data: EmojiData): Map<string, string> {
  const index = new Map<string, string>();
  for (const emoji of flattenEmojis(data)) {
    index.set(emoji.char, buildSearchTextForLocales(emoji, [enTranslation as TranslationDict]));
  }
  return index;
}

// Build full index with all languages (async, for cross-language search)
export async function buildFullSearchIndex(data: EmojiData): Promise<Map<string, string>> {
  const [es, ru, pt] = await Promise.all([
    import('../locales/es/translation.json'),
    import('../locales/ru/translation.json'),
    import('../locales/pt/translation.json'),
  ]);
  const allLocales = [
    enTranslation as TranslationDict,
    es.default as TranslationDict,
    ru.default as TranslationDict,
    pt.default as TranslationDict,
  ];
  const index = new Map<string, string>();
  for (const emoji of flattenEmojis(data)) {
    index.set(emoji.char, buildSearchTextForLocales(emoji, allLocales));
  }
  return index;
}
